if (!customElements.get('cart-configured')) {
  customElements.define(
    'cart-configured',
    class CartConfigured extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && event.target.matches('[data-cart-size-input]')) {
            event.preventDefault();
            this.saveQuantities(event.target.closest('[data-cart-group]'));
          }
        });
        this.addEventListener('input', (event) => {
          if (!event.target.matches('[data-cart-size-input]')) return;
          this.clampToStock(event.target);
          const group = event.target.closest('[data-cart-group]');
          const pills = Array.from(group.querySelectorAll('[data-cart-size]'));
          this.updateSurcharge(
            group,
            pills.map((pill) => parseInt(pill.querySelector('[data-cart-size-input]').value, 10) || 0)
          );
        });
        // While quantities are being edited, the first click outside the sizes box only saves them:
        // it is caught before anything else, so no link or button on the page reacts to it.
        document.addEventListener(
          'click',
          (event) => {
            const editing = this.querySelector('[data-cart-group].is-editing');
            if (!editing || editing.querySelector('[data-cart-sizes]').contains(event.target)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.saveQuantities(editing);
          },
          true
        );
        document.addEventListener('click', (event) => {
          if (event.target.closest && event.target.closest('.cart-custom__duplicate')) return;
          this.closeDuplicatePanels();
        });
        this.removeOrphanLines();
      }

      // Print lines whose garments are gone: hidden by the section but still charged, so take them out.
      async removeOrphanLines() {
        const keys = (this.dataset.orphanLines || '').split(',').filter(Boolean);
        if (!keys.length) return;
        delete this.dataset.orphanLines;
        const updates = Object.fromEntries(keys.map((key) => [key, 0]));
        try {
          const response = await this.post('/cart/update.js', { updates });
          if (!this.renderSections(response.sections)) await this.refresh();
        } catch (error) {
          this.showError(error.message || 'Something went wrong. Please refresh the page.');
        }
      }

      onClick(event) {
        const group = event.target.closest('[data-cart-group]');
        if (!group) return;

        if (event.target.closest('[data-cart-remove]')) {
          this.removeGroup(group);
        } else if (event.target.closest('[data-cart-duplicate-toggle]')) {
          this.toggleDuplicatePanel(group);
        } else if (event.target.closest('[data-cart-duplicate-color]')) {
          this.duplicateGroup(group, event.target.closest('[data-cart-duplicate-color]').dataset.cartDuplicateColor);
        } else if (event.target.closest('[data-cart-edit]')) {
          if (group.classList.contains('is-editing')) {
            this.saveQuantities(group);
          } else {
            this.startEditing(group);
          }
        }
      }

      groupData(group) {
        const data = JSON.parse(group.querySelector('[data-cart-group-data]').textContent);
        // Liquid serializes line properties as [[name, value], ...]: turn them into an object.
        data.lines.forEach((line) => {
          if (Array.isArray(line.properties)) line.properties = Object.fromEntries(line.properties);
        });
        return data;
      }

      /* ------------------------------------------------------------ remove */

      async removeGroup(group) {
        const { lines } = this.groupData(group);
        const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
        await this.run(group, () => this.post('/cart/update.js', { updates }));
      }

      /* --------------------------------------------------------- duplicate */

      toggleDuplicatePanel(group) {
        const panel = group.querySelector('[data-cart-duplicate-panel]');
        const toggle = group.querySelector('[data-cart-duplicate-toggle]');
        const willOpen = panel.hidden;
        this.closeDuplicatePanels();
        panel.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', String(willOpen));
      }

      closeDuplicatePanels() {
        this.querySelectorAll('[data-cart-duplicate-panel]').forEach((panel) => {
          panel.hidden = true;
        });
        this.querySelectorAll('[data-cart-duplicate-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
        });
      }

      async duplicateGroup(group, newColor) {
        const data = this.groupData(group);
        const newGroupId = this.newGroupId();
        const missing = [];
        let addonCount = 0;

        const items = data.lines
          .map((line) => {
            const properties = { ...line.properties, '_Bulk group': newGroupId };
            if (line.addon) {
              addonCount += 1;
              if (properties.For) properties.For = properties.For.replace(data.colorValue, newColor);
              return { id: line.variantId, quantity: line.quantity, properties };
            }

            // Same options (size...), with the chosen color instead of the current one.
            const wanted = line.options.map((value) => (value === data.colorValue ? newColor : value));
            const variant = data.variants.find(
              (v) => v.available && v.options.every((value, index) => value === wanted[index])
            );
            const sizeLabel = line.options.filter((value) => value !== data.colorValue).join(' / ');
            if (!variant || variant.max === 0) {
              missing.push(sizeLabel);
              return null;
            }
            // Above the stock, Shopify adds what it can and returns its own "Only x items were added" message.
            return { id: variant.id, quantity: line.quantity, properties };
          })
          .filter(Boolean);

        if (items.length <= addonCount) {
          this.showError(`${newColor} is not available in these sizes.`);
          return;
        }

        const message = missing.length ? `Not available in ${newColor}: ${missing.join(', ')}.` : null;
        await this.run(group, () => this.post('/cart/add.js', { items }), message);
      }

      newGroupId() {
        return (() => {
          // Short readable code shared by all lines of one configured item, e.g. "#A7K3"
          // (no 0/O/1/I, to read it easily on orders).
          const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
          const values = new Uint8Array(4);
          if (window.crypto?.getRandomValues) window.crypto.getRandomValues(values);
          else values.forEach((_, i) => (values[i] = Math.floor(Math.random() * 256)));
          return '#' + Array.from(values, (v) => alphabet[v % alphabet.length]).join('');
        })();
      }

      /* ---------------------------------------------------- edit quantities */

      startEditing(group) {
        this.querySelectorAll('[data-cart-group].is-editing').forEach((other) => {
          if (other !== group) this.cancelEditing(other);
        });
        group.classList.add('is-editing');
        group.querySelector('[data-cart-edit-label]').textContent = 'Save Quantities';
      }

      cancelEditing(group) {
        group.classList.remove('is-editing');
        group.querySelector('[data-cart-edit-label]').textContent = 'Edit Quantities';
        group.querySelectorAll('[data-cart-size]').forEach((pill) => {
          pill.querySelector('[data-cart-size-input]').value = pill.dataset.quantity;
        });
        this.updateSurcharge(
          group,
          Array.from(group.querySelectorAll('[data-cart-size]')).map((pill) => Number(pill.dataset.quantity))
        );
      }

      // Same as the product page: above the available stock, the field shakes and snaps back to the max.
      clampToStock(input) {
        if (input.dataset.max === undefined) return;
        const max = Number(input.dataset.max);
        if ((parseInt(input.value, 10) || 0) <= max) return;

        input.value = String(max);
        input.classList.remove('is-shaking');
        void input.offsetWidth; // restart the animation even if it's already playing
        input.classList.add('is-shaking');
        input.addEventListener('animationend', () => input.classList.remove('is-shaking'), { once: true });
      }

      async saveQuantities(group) {
        if (group.dataset.saving) return; // already saving: don't send the same change twice
        group.dataset.saving = 'true';
        try {
          await this.saveQuantitiesNow(group);
        } finally {
          delete group.dataset.saving;
        }
      }

      async saveQuantitiesNow(group) {
        const data = this.groupData(group);
        const pills = Array.from(group.querySelectorAll('[data-cart-size]'));
        const wanted = pills.map((pill) => ({
          size: pill.dataset.size,
          variantId: Number(pill.dataset.variantId),
          key: pill.dataset.lineKey,
          current: Number(pill.dataset.quantity),
          quantity: Math.max(0, parseInt(pill.querySelector('[data-cart-size-input]').value, 10) || 0),
        }));

        if (wanted.every((entry) => entry.quantity === entry.current)) {
          this.cancelEditing(group);
          return;
        }

        const totalPieces = wanted.reduce((sum, entry) => sum + entry.quantity, 0);
        if (totalPieces === 0) {
          await this.removeGroup(group);
          return;
        }

        this.showQuantitiesNow(group, wanted);

        await this.run(group, async () => {
          // Existing sizes get their new quantity (0 removes the line); add-ons follow the number of pieces.
          const updates = {};
          wanted.filter((entry) => entry.key).forEach((entry) => (updates[entry.key] = entry.quantity));
          data.lines.filter((line) => line.addon).forEach((line) => (updates[line.key] = totalPieces));

          // New sizes join the same article, with the same details.
          const template = data.lines.find((line) => !line.addon);
          const newItems = wanted
            .filter((entry) => !entry.key && entry.quantity > 0)
            .map((entry) => ({ id: entry.variantId, quantity: entry.quantity, properties: { ...template.properties } }));

          if (!newItems.length) return this.post('/cart/update.js', { updates });
          await this.post('/cart/update.js', { updates }, false);
          return this.post('/cart/add.js', { items: newItems });
        }, null, 'is-updating');
      }

      // Show the new quantities right away; prices follow when Shopify answers.
      showQuantitiesNow(group, wanted) {
        const pills = group.querySelectorAll('[data-cart-size]');
        pills.forEach((pill, index) => {
          const quantity = wanted[index].quantity;
          pill.querySelector('.cart-custom__pill-qty').textContent = quantity;
          pill.classList.toggle('is-empty', quantity === 0);
        });
        const pieces = wanted.reduce((sum, entry) => sum + entry.quantity, 0);
        const sizes = wanted.filter((entry) => entry.quantity > 0).length;
        const summary = group.querySelectorAll('.cart-custom__sizes-summary strong');
        const unit = group.hasAttribute('data-cart-dtf') ? 'transfer' : 'piece';
        if (summary[0]) summary[0].textContent = `${pieces} ${unit}${pieces === 1 ? '' : 's'}`;
        if (summary[1]) summary[1].textContent = `${sizes} ${sizes === 1 ? 'size' : 'sizes'}`;
        this.updateSurcharge(
          group,
          wanted.map((entry) => entry.quantity)
        );
        group.classList.remove('is-editing');
        group.querySelector('[data-cart-edit-label]').textContent = 'Edit Quantities';
      }

      // Same note as in the configurator: "3 x XXL is +$6.00", for sizes priced above size S.
      updateSurcharge(group, quantities) {
        const note = group.querySelector('[data-cart-surcharge]');
        if (!note) return;
        const basePrice = Number(note.dataset.basePrice);
        const pills = Array.from(group.querySelectorAll('[data-cart-size]'));
        const lines = pills
          .map((pill, index) => {
            const qty = quantities[index] || 0;
            const extra = Number(pill.dataset.price) - basePrice;
            if (qty <= 0 || extra <= 0) return null;
            return note.dataset.template
              .replace('%qty%', qty)
              .replace('%size%', pill.dataset.size)
              .replace('%amount%', this.formatMoney((extra * qty) / 100));
          })
          .filter(Boolean);

        note.querySelector('[data-cart-surcharge-text]').innerHTML = lines.map((line) => `<span>${line}</span>`).join('');
        note.hidden = !lines.length;
      }

      formatMoney(amount) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.dataset.currency || 'USD',
          }).format(amount);
        } catch (error) {
          return `$${amount.toFixed(2)}`;
        }
      }

      /* ------------------------------------------------------------ helpers */

      // loadingClass: 'is-loading' greys out the whole article, 'is-updating' only the prices.
      async run(group, action, messageAfter = null, loadingClass = 'is-loading') {
        this.showError(null);
        group.classList.add(loadingClass);
        this.classList.toggle('is-updating', loadingClass === 'is-updating');
        try {
          const response = await action();
          if (!this.renderSections(response?.sections)) await this.refresh();
          if (messageAfter) this.showError(messageAfter);
        } catch (error) {
          // Shopify may have applied part of the change (e.g. added what was in stock): show the real cart.
          await this.refresh().catch(() => group.classList.remove(loadingClass));
          this.showError(error.message || 'Something went wrong. Please try again.');
        } finally {
          this.classList.remove('is-updating');
        }
      }

      // withSections: ask Shopify to return the re-rendered cart and cart icon in the same response.
      async post(url, body, withSections = true) {
        const payload = withSections
          ? {
              ...body,
              sections: [this.dataset.sectionId, 'cart-icon-bubble'],
              sections_url: window.location.pathname + window.location.search,
            }
          : body;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const json = await response.json();
        if (!response.ok || json.status) throw new Error(json.description || json.message || 'Cart update failed.');
        return json;
      }

      async get(url) {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        return response.json();
      }

      renderSections(sections) {
        const sectionHtml = sections?.[this.dataset.sectionId];
        if (!sectionHtml) return false;

        const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('cart-configured');
        if (!fresh) return false;
        this.innerHTML = fresh.innerHTML;

        const bubbleTarget = document.getElementById('cart-icon-bubble');
        if (sections['cart-icon-bubble'] && bubbleTarget) {
          const bubbleHtml = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const content = bubbleHtml.querySelector('.shopify-section');
          if (content) bubbleTarget.innerHTML = content.innerHTML;
        }
        return true;
      }

      async refresh() {
        const [sectionHtml, bubble] = await Promise.all([
          fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`).then((r) => r.text()),
          fetch(`${window.location.pathname}?sections=cart-icon-bubble`)
            .then((r) => r.json())
            .catch(() => null),
        ]);
        const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('cart-configured');
        if (fresh) this.innerHTML = fresh.innerHTML;

        const bubbleTarget = document.getElementById('cart-icon-bubble');
        if (bubble && bubble['cart-icon-bubble'] && bubbleTarget) {
          const bubbleHtml = new DOMParser().parseFromString(bubble['cart-icon-bubble'], 'text/html');
          const content = bubbleHtml.querySelector('.shopify-section');
          if (content) bubbleTarget.innerHTML = content.innerHTML;
        }
      }

      showError(message) {
        const el = this.querySelector('[data-cart-error]');
        if (!el) return;
        el.textContent = message || '';
        el.hidden = !message;
      }
    }
  );
}
